import makeMutation from "@saleor/hooks/makeMutation";
import gql from "graphql-tag";

import {
  DropOffLocationCreateMutation,
  DropOffLocationCreateMutationVariables
} from "./types/DropOffLocationCreateMutation";
import {
  DropOffLocationUpdateMutation,
  DropOffLocationUpdateMutationVariables
} from "./types/DropOffLocationUpdateMutation";
import { RackCreate, RackCreateVariables } from "./types/RackCreate";
import { RackDelete, RackDeleteVariables } from "./types/RackDelete";
import { RackUpdate, RackUpdateVariables } from "./types/RackUpdate";
import {
  TaskAreaAddStoresMutation,
  TaskAreaAddStoresMutationVariables
} from "./types/TaskAreaAddStoresMutation";
import {
  TaskAreaCreateMutation,
  TaskAreaCreateMutationVariables
} from "./types/TaskAreaCreateMutation";
import {
  TaskAreaRemoveStoresMutation,
  TaskAreaRemoveStoresMutationVariables
} from "./types/TaskAreaRemoveStoresMutation";
import {
  TaskAreaUpdateMutation,
  TaskAreaUpdateMutationVariables
} from "./types/TaskAreaUpdateMutation";
import {
  TemplateCreateMutation,
  TemplateCreateMutationVariables
} from "./types/TemplateCreateMutation";
import {
  TemplateDeleteMutation,
  TemplateDeleteMutationVariables
} from "./types/TemplateDeleteMutation";
import {
  TemplateDuplicateMutation,
  TemplateDuplicateMutationVariables
} from "./types/TemplateDuplicateMutation";
import {
  TemplateFinalizeMutation,
  TemplateFinalizeMutationVariables
} from "./types/TemplateFinalizeMutation";
import {
  TemplateRenameMutation,
  TemplateRenameMutationVariables
} from "./types/TemplateRenameMutation";
import {
  TemplateUpdateMutation,
  TemplateUpdateMutationVariables
} from "./types/TemplateUpdateMutation";

const dropOffLocationCreateMutation = gql`
  mutation DropOffLocationCreateMutation($input: DropOffLocationCreateInput!) {
    dropOffLocationCreate(input: $input) {
      dropOffLocation {
        id
        mall {
          id
          name
        }
        name
        code
        template {
          id
        }
      }
      dropOffLocationError {
        field
        message
        code
      }
    }
  }
`;

const templateCreateMutation = gql`
  mutation TemplateCreateMutation($input: TemplateCreateInput!) {
    templateCreate(input: $input) {
      template {
        id
        name
        mall {
          id
          name
        }
      }
      templateError {
        field
        message
      }
    }
  }
`;

const taskAreaCreateMutation = gql`
  mutation TaskAreaCreateMutation($input: TaskAreaCreateInput!) {
    taskAreaCreate(input: $input) {
      taskArea {
        id
        name
        code
        description
        createdAt
        updatedAt
        status
      }
      taskAreaError {
        field
        message
      }
    }
  }
`;

const dropOffLocationUpdateMutation = gql`
  mutation DropOffLocationUpdateMutation(
    $input: DropOffLocationUpdateInput!
    $id: ID!
  ) {
    dropOffLocationUpdate(input: $input, id: $id) {
      dropOffLocation {
        id
        name
        mall {
          id
          name
        }
      }
      dropOffLocationError {
        field
        message
      }
    }
  }
`;

export const useDropOffLocationCreate = makeMutation<
  DropOffLocationCreateMutation,
  DropOffLocationCreateMutationVariables
>(dropOffLocationCreateMutation);

export const useDropOffLocationUpdate = makeMutation<
  DropOffLocationUpdateMutation,
  DropOffLocationUpdateMutationVariables
>(dropOffLocationUpdateMutation);

export const useTemplateCreate = makeMutation<
  TemplateCreateMutation,
  TemplateCreateMutationVariables
>(templateCreateMutation);

export const useTaskAreaCreate = makeMutation<
  TaskAreaCreateMutation,
  TaskAreaCreateMutationVariables
>(taskAreaCreateMutation);

const taskAreaUpdateMutation = gql`
  mutation TaskAreaUpdateMutation($input: TaskAreaUpdateInput!, $id: ID!) {
    taskAreaUpdate(input: $input, id: $id) {
      taskArea {
        id
        name
        status
        code
        status
        dropOffLocation {
          id
          name
          code
          status
        }
      }
      taskAreaError {
        field
        message
      }
    }
  }
`;

export const useTaskAreaUpdate = makeMutation<
  TaskAreaUpdateMutation,
  TaskAreaUpdateMutationVariables
>(taskAreaUpdateMutation);

const taskAreaAddStoresMutation = gql`
  mutation TaskAreaAddStoresMutation($input: TaskAreadAddRemoveStoresInput!) {
    taskAreaAddStores(input: $input) {
      taskArea {
        id
        name
        status
        code
        status
        dropOffLocation {
          id
          name
          code
          status
        }
      }
      taskAreaError {
        field
        message
      }
    }
  }
`;

export const useTaskAreaAddStores = makeMutation<
  TaskAreaAddStoresMutation,
  TaskAreaAddStoresMutationVariables
>(taskAreaAddStoresMutation);

const taskAreaRemoveStoresMutation = gql`
  mutation TaskAreaRemoveStoresMutation(
    $input: TaskAreadAddRemoveStoresInput!
  ) {
    taskAreaRemoveStores(input: $input) {
      taskArea {
        id
        name
        status
        code
        status
        dropOffLocation {
          id
          name
          code
          status
        }
      }
      taskAreaError {
        field
        message
      }
    }
  }
`;

export const useTaskAreaRemoveStores = makeMutation<
  TaskAreaRemoveStoresMutation,
  TaskAreaRemoveStoresMutationVariables
>(taskAreaRemoveStoresMutation);

const templateFinalizeMutation = gql`
  mutation TemplateFinalizeMutation($id: ID!) {
    templateFinalize(id: $id) {
      template {
        id
        name
        mall {
          id
          name
        }
      }
      templateError {
        field
        message
      }
    }
  }
`;

export const useTemplateFinalize = makeMutation<
  TemplateFinalizeMutation,
  TemplateFinalizeMutationVariables
>(templateFinalizeMutation);

const templateUpdateMutation = gql`
  mutation TemplateUpdateMutation($id: ID!, $input: TemplateUpdateInput!) {
    templateUpdate(id: $id, input: $input) {
      template {
        id
        name
        mall {
          id
          name
        }
      }
      templateError {
        field
        message
      }
    }
  }
`;

export const useTemplateUpdate = makeMutation<
  TemplateUpdateMutation,
  TemplateUpdateMutationVariables
>(templateUpdateMutation);

const templateDuplicateMutation = gql`
  mutation TemplateDuplicateMutation($id: ID!) {
    templateDuplicate(id: $id) {
      template {
        id
        name
        mall {
          id
          name
        }
      }
      taskAreaError {
        field
        message
      }
    }
  }
`;

export const useTemplateDuplicate = makeMutation<
  TemplateDuplicateMutation,
  TemplateDuplicateMutationVariables
>(templateDuplicateMutation);

const templateDeleteMutation = gql`
  mutation TemplateDeleteMutation($id: ID!) {
    templateDelete(id: $id) {
      template {
        id
        name
        mall {
          id
          name
        }
      }
      templateError {
        field
        message
      }
    }
  }
`;

export const useTemplateDelete = makeMutation<
  TemplateDeleteMutation,
  TemplateDeleteMutationVariables
>(templateDeleteMutation);

const templateRenameMutation = gql`
  mutation TemplateRenameMutation($id: ID!, $name: String!) {
    templateRename(id: $id, name: $name) {
      template {
        id
        name
        mall {
          id
          name
        }
      }
      taskAreaError {
        field
        message
      }
    }
  }
`;

export const useTemplateRename = makeMutation<
  TemplateRenameMutation,
  TemplateRenameMutationVariables
>(templateRenameMutation);

const rackCreateMutation = gql`
  mutation RackCreate($input: RackCreateInput!) {
    rackCreate(input: $input) {
      rack {
        id
        prefix
        name
        dropOffLocation {
          id
          name
          template {
            id
            name
          }
        }
      }
      bins {
        binNo
        isAvailable
      }
      rackError {
        field
        message
        code
      }
    }
  }
`;

export const useRackCreate = makeMutation<RackCreate, RackCreateVariables>(
  rackCreateMutation
);

const rackDeleteMutation = gql`
  mutation RackDelete($id: ID!) {
    rackDelete(rackId: $id) {
      rack {
        id
      }
      rackError {
        field
        message
        code
      }
    }
  }
`;

export const useRackDelete = makeMutation<RackDelete, RackDeleteVariables>(
  rackDeleteMutation
);

const rackUpdateMutation = gql`
  mutation RackUpdate($input: RackCreateInput!, $id: ID!) {
    rackUpdate(input: $input, id: $id) {
      rack {
        id
        prefix
        name
        dropOffLocation {
          id
          name
          template {
            id
            name
          }
        }
      }
      bins {
        binNo
        isAvailable
      }
      rackError {
        field
        message
        code
      }
    }
  }
`;

export const useRackUpdate = makeMutation<RackUpdate, RackUpdateVariables>(
  rackUpdateMutation
);
